import React from "react";
import { connect } from "react-redux";

import PurchaseService from "services/purchase";
import PurchaseItemService from "services/purchase-item";

import {
  update,
  create,
  remove,
  updateItem,
  createItem,
  removeItem,
} from "reducers/purchase";
import { setBreadcrumbs } from "reducers/view";

import ZeeForm from "components/ZeeForm";
import ZeeTable from "components/ZeeTable";

import Button from "@material-ui/core/Button";

class CreatePurchase extends React.Component {
  purchaseService = new PurchaseService();
  purchaseItemService = new PurchaseItemService();

  form = {
    pages: [
      {
        inputs: [
          {
            name: "Date purchaseed",
            type: "Date",
            value: "date",
          },
          {
            name: "Supplier",
            type: "Select",
            value: "supplier_id",
            items: [{ name: "Please select" }, ...this.suppliers],
          },
          {
            name: "Supplier reference",
            type: "Textbox",
            value: "supplier_reference",
          },
        ],
      },
    ],
  };

  state = {
    supplier_id: this.currentItem?.supplier_id ?? null,
    supplier_reference: this.currentItem?.supplier_reference ?? null,
    date: this.currentItem?.date ?? Math.round(Date.now() / 1000),
    items: this.currentItems ?? [],
  };

  columns = {
    quantity: {
      name: "Quantity",
    },
    stock_id: {
      name: "Stock",
      values: [{ name: "Please select" }, ...this.stocks],
      format: (stockId, row) => this.getStockById(stockId)?.name,
    },
    partnumber: {
      name: "Part Number",
    },
    price: {
      name: "Price",
      format: (price, row) => this.getPrice(row),
    },
    //discount: { name: "Discount %" },
    subtotal: {
      name: "Subtotal",
      noEdit: true,
      format: (subtotal, row) => this.getSubtotal(row),
    },
  };

  constructor() {
    super(...arguments);
    if (this.isEditing) {
      this.props.setBreadcrumbs([
        { name: "Purchases", slug: "/purchases" },
        { name: "Editing Purchase", slug: `/purchases/${this.currentId}` },
      ]);
    } else {
      this.props.setBreadcrumbs([
        { name: "Purchases", slug: "/purchases" },
        { name: "Create Purchase", slug: "/purchases/create" },
      ]);
    }
  }

  getSubtotal(row) {
    const price = this.getPrice(row);
    const quantity = row.quantity > 0 ? row.quantity : 1;

    if (!price) {
      return 0;
    }

    const total = price * quantity;

    return total.toFixed(2);
  }

  getPrice(row) {
    return row.price;
  }

  get currentId() {
    return this.props.match.params.id ?? null;
  }

  get currentItem() {
    return this.props.purchases.find((item) => item.id === this.currentId);
  }

  get currentItems() {
    return this.props.purchaseItem.filter(
      (item) => item.purchase_id === this.currentId
    );
  }

  get isEditing() {
    return this.currentId ? true : false;
  }

  get items() {
    return this.state.items;
  }

  get suppliers() {
    return this.props.supplier?.map(({ id, name }) => ({ id, name })) ?? [];
  }

  get stocks() {
    return this.props.stock?.map(({ id, name }) => ({ id, name })) ?? [];
  }

  getStockById(stockId) {
    return this.props.stock?.find(
      (item) => parseInt(item.id) === parseInt(stockId)
    );
  }

  getItemById(itemId) {
    return this.items?.find((item) => parseInt(item.id) === parseInt(itemId));
  }

  onEdit(row) {
    if (!row.stock_id) {
      return false;
    }

    const existing = this.getItemById(row.id);

    row.quantity = row.quantity > 0 ? row.quantity : 1;

    row.price = this.getPrice(row);
    if (parseInt(existing.stock_id) !== parseInt(row.stock_id)) {
      row.price = this.getPrice({ ...row, price: null });
    }

    const items = this.items.map((item) =>
      item.id === row.id ? { ...item, ...row } : item
    );

    this.setState({ items });

    return true;
  }

  onDelete(row) {
    const items = this.items.reduce(
      (agg, item) => (item.id === row.id ? agg : [...agg, item]),
      []
    );

    this.setState({ items });
  }

  onAdd(row) {
    if (!row.stock_id) {
      return false;
    }

    row.isNew = true;
    row.quantity = row.quantity > 0 ? row.quantity : 1;
    row.price = this.getPrice(row);

    this.setState({
      items: [...this.items, row],
    });

    return true;
  }

  onUpdate(data) {
    this.setState({
      ...data,
    });
  }

  async onConfirm() {
    const { supplier_id, date, items, supplier_reference } = this.state;

    if (!supplier_id || !date) {
      return;
    }

    const purchase = {
      supplier_id,
      supplier_reference,
      date,
    };

    if (this.currentId) {
      // Update
      const result = await this.purchaseService
        .update({ id: this.currentId, ...purchase })
        .then(({ data }) => data);

      if (result.success) {
        this.props.update(result.data);

        const toDelete = this.currentItems.reduce(
          (agg, item) =>
            items.find((item2) => item.id === item2.id) ? agg : [...agg, item],
          []
        );

        const toUpdate = items.reduce(
          (agg, item) => (item?.isNew ? agg : [...agg, item]),
          []
        );

        const toAdd = items.reduce(
          (agg, item) => (item?.isNew ? [...agg, item] : agg),
          []
        );

        const updateResultItems = await Promise.all(
          toUpdate.map(async (item) =>
            this.purchaseItemService.update(item).then(({ data }) => data)
          )
        );

        updateResultItems.forEach((item) => this.props.updateItem(item.data));

        const createResultItems = await Promise.all(
          toAdd.map(async (item) =>
            this.purchaseItemService
              .create({ ...item, purchase_id: this.currentId })
              .then(({ data }) => data)
          )
        );

        createResultItems.forEach((item) => this.props.createItem(item.data));

        const deleteResultItems = await Promise.all(
          toDelete.map(async (item) =>
            this.purchaseItemService.delete(item).then(({ data }) => data)
          )
        );

        deleteResultItems.forEach((item) => this.props.removeItem(item.data));

        return true;
      }

      return false;
    }

    if (!items || items?.length === 0) {
      return null;
    }

    // Create
    const result = await this.purchaseService
      .create(purchase)
      .then(({ data }) => data);

    if (result.success) {
      const { id } = result.data;
      this.props.create(result.data);

      const resultItems = await Promise.all(
        items.map(async (item) =>
          this.purchaseItemService
            .create({ ...item, purchase_id: id })
            .then(({ data }) => data)
        )
      );

      resultItems.forEach((item) => this.props.createItem(item.data));

      this.props.history.push(`/purchases/${id}`);

      return true;
    }

    return false;
  }

  onCancel() {
    this.props.history.push("/purchases");
  }

  render() {
    return (
      <div className="form">
        {this.isEditing ? (
          <h1>Edit purchase</h1>
        ) : (
          <h1>Create a new purchase</h1>
        )}

        <ZeeForm
          form={this.form}
          data={this.state}
          onUpdate={(data) => this.onUpdate(data)}
        />
        <ZeeTable
          columns={this.columns}
          data={this.items}
          hasAdd={true}
          hasEdit={true}
          hasDelete={true}
          onAdd={(row) => this.onAdd(row)}
          onEdit={(row) => this.onEdit(row)}
          onDelete={(row) => this.onDelete(row)}
        />
        <Button
          variant="contained"
          color="primary"
          onClick={() => this.onConfirm()}
        >
          {this.isEditing ? "Update" : "Create"}
        </Button>
        <Button color="secondary" onClick={() => this.onCancel()}>
          Cancel
        </Button>
      </div>
    );
  }
}

export default connect(
  (state) => {
    return {
      stock: state.stock.all,
      supplier: state.supplier.all,
      purchases: state.purchase.all,
      purchaseItem: state.purchase.item.all,
    };
  },
  { update, create, remove, updateItem, createItem, removeItem, setBreadcrumbs }
)(CreatePurchase);
